"use client";

import { createContext, useContext, useEffect, useState } from "react";

type Theme = "light" | "dark";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  mounted: boolean;
};

const initialState: ThemeProviderState = {
  theme: "light",
  setTheme: () => null,
  toggleTheme: () => null,
  mounted: false,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = "light",
  storageKey = "theme",
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    // 在客户端，从阻塞脚本设置的DOM类中读取初始主题
    if (typeof window !== "undefined") {
      if (document.documentElement.classList.contains('dark')) {
        return 'dark';
      } else if (document.documentElement.classList.contains('light')) {
        return 'light';
      }
    }
    return defaultTheme;
  });
  const [mounted, setMounted] = useState(false);

  // Handle mounting - 只设置mounted状态，不做其他操作
  useEffect(() => {
    setMounted(true);
  }, []);

  // 只在用户主动改变主题时更新DOM和localStorage
  const updateTheme = (newTheme: Theme) => {
    setTheme(newTheme);
    
    if (typeof window !== "undefined") {
      const root = document.documentElement;
      root.classList.remove("light", "dark");
      root.classList.add(newTheme);
      localStorage.setItem(storageKey, newTheme);
    }
  };

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    updateTheme(newTheme);
  };

  const value = {
    theme,
    setTheme: updateTheme,
    toggleTheme,
    mounted,
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
